import { MouseEventHandler, ReactNode, Ref } from 'react';

import { ButtonVariant, CtaLinkProps, HeadingTagType } from '@purpur/library';

import { DeliveryEntry } from './entries/DeliveryEntry';
import { DiscountEntry } from './entries/DiscountEntry';
import { DisneyVas } from './entries/DisneyVas';
import { GiftEntry } from './entries/GiftEntry';
import { HboMaxVas } from './entries/HboMaxVas';
import { InsuranceEntry } from './entries/InsuranceEntry';
import { MainProductEntry } from './entries/MainProductEntry';
import { MobRatePlanEntry } from './entries/MobRatePlanEntry';
import { NetflixVas } from './entries/NetflixVas';
import { PromotionEntry } from './entries/PromotionEntry';
import { ServiceEntry } from './entries/ServiceEntry';
import { SimCardEntry } from './entries/SimCardEntry';
import { TvPlanEntry } from './entries/TvPlanEntry';
import { UpsellEntry } from './entries/UpsellEntry';

type CTALinkVariant = CtaLinkProps['variant'];

export const POSSIBLE_CART_ENTRIES = {
    MainProductEntry,
    InsuranceEntry,
    UpsellEntry,
    GiftEntry,
    SimCardEntry,
    MobRatePlanEntry,
    ServiceEntry,
    PromotionEntry,
    TvPlanEntry,
    NetflixVas,
    DisneyVas,
    HboMaxVas,
    DeliveryEntry,
    DiscountEntry,
} as const;
export type ExpansionMode = 'NEVER' | 'MOBILE_ONLY' | 'ALWAYS';

export type ExpansionModeAttr = { $expansionMode: ExpansionMode };

export type CartHeaderData = {
    expandable: ExpansionMode;
    isDefaultExpanded: boolean;
    isForceExpanded: boolean;
    title: string;
    titleTag?: HeadingTagType;
};

export type Price = { value: number; formattedValue: string };

export type ImageData = { url: string; alt: string };

export type CartTotalsData = {
    monthlyPrice?: Price;
    totalPrice: Price;
    totalPriceLabel: string;
    isTotalMonthlyPrice?: boolean;
    futurePrice?: Price;
    futurePriceDate?: string;
    cartTotalsRef?: Ref<HTMLElement>;
};

export type ButtonHandler = {
    onClick: MouseEventHandler;
    variant?: ButtonVariant;
    hasArrow: boolean;
    isDisabled?: boolean;
    isLoading?: boolean;
    text: string;
};
export type LinkHandler = {
    onClick?: MouseEventHandler;
    url: string;
    variant?: CTALinkVariant;
    hasArrow: boolean;
    text: string;
};
export type FormSubmitHandler = {
    type: 'submit';
    onClick?: MouseEventHandler;
    variant?: ButtonVariant;
    hasArrow: boolean;
    isDisabled?: boolean;
    isLoading?: boolean;
    text: string;
};
export type CartActionData = ButtonHandler | LinkHandler | FormSubmitHandler;
export type CartSecondaryActionData = ButtonHandler | LinkHandler;

export type CartLegalTextData = { legalText: string };

export type PreorderBannerData = { shipmentDate: string };

export type CartTotalsNotificationData = { content: ReactNode };

type IsVisible<T> = (T & { isVisible: true }) | ({ isVisible: false } & Partial<T>);

export interface OrderCartProps {
    children: ReactNode;
    header: CartHeaderData;
    cartTotals: CartTotalsData;
    preorderBanner: IsVisible<PreorderBannerData>;
    cartAction: IsVisible<CartActionData>;
    cartSecondaryAction: IsVisible<CartSecondaryActionData>;
    cartOutOfStock: { isVisible: boolean };
    cartValuePrompts: { isVisible: boolean };
    cartVatNote: { isVisible: boolean };
    cartLegalText: IsVisible<CartLegalTextData>;
    cartTotalsNotification: IsVisible<CartTotalsNotificationData>;
    className?: string;
    hidden?: boolean;
    'data-test'?: string;
}

export const isTypeLinkHandler = (action: CartActionData): action is LinkHandler => {
    return 'url' in action && typeof action.url === 'string';
};

export const isTypeFormSubmitHandler = (action: CartActionData): action is FormSubmitHandler => {
    return 'type' in action && action.type === 'submit';
};
